import type { PTConfig, UTMParams, SDKModule, SDKContext, PreviewActionDetail } from '../types';
import { EventBus } from './eventBus';
import { getSessionId, collectUTM } from '../utils/session';
import { trackPtengine } from '../utils/track';
import { post } from '../utils/http';
import { ENDPOINT } from '../config';
import { log } from '../utils/logger';

/** Dispatch a preview-mode action to a CustomEvent so editors can render UI feedback. */
function dispatchPreviewAction(detail: PreviewActionDetail): void {
  try {
    document.dispatchEvent(new CustomEvent('pt-preview-action', { detail }));
  } catch (e) {
    log('warn', 'dispatch pt-preview-action failed', e);
  }
}

/**
 * SDK core — manages module lifecycle and provides shared context.
 */
export class SDK {
  private modules: SDKModule[] = [];
  private bus = new EventBus();
  private _sessionId: string;
  private _utm: UTMParams;

  readonly config: PTConfig;
  readonly endpoint: string;
  readonly preview: boolean;

  constructor(config: PTConfig) {
    this.config = config;
    this.endpoint = ENDPOINT;
    this.preview = !!config.preview;
    this._sessionId = getSessionId();
    this._utm = collectUTM();
  }

  get sessionId(): string {
    return this._sessionId;
  }

  get utm(): UTMParams {
    return this._utm;
  }

  /** Create the context object passed to modules */
  createContext(): SDKContext {
    const preview = this.preview;

    const trackImpl = (name: string, props?: Record<string, unknown>): void => {
      if (preview) {
        log('info', 'preview: skipped track ' + name, props);
        dispatchPreviewAction({ kind: 'track', reason: name, payload: props });
        return;
      }
      trackPtengine(name, props);
    };

    const postImpl = (path: string, data: unknown): Promise<void> => {
      if (preview) {
        log('info', 'preview: skipped POST ' + path, data);
        dispatchPreviewAction({ kind: 'post', reason: path, payload: data });
        return Promise.resolve();
      }
      return post(this.endpoint + path, data);
    };

    return {
      config: this.config,
      endpoint: this.endpoint,
      sessionId: this._sessionId,
      utm: this._utm,
      preview,
      track: trackImpl,
      post: postImpl,
      on: (event, cb) => this.bus.on(event, cb),
      off: (event, cb) => this.bus.off(event, cb),
      emit: (event, data) => this.bus.emit(event, data),
    };
  }

  /** Register and initialize a module */
  use(module: SDKModule): void {
    this.modules.push(module);
    try {
      module.init(this.createContext());
    } catch (e) {
      log('error', 'module init failed: ' + module.name, e);
    }
  }

  /** Event bus delegation */
  on(event: string, callback: (data?: unknown) => void): void {
    this.bus.on(event, callback);
  }

  off(event: string, callback: (data?: unknown) => void): void {
    this.bus.off(event, callback);
  }

  emit(event: string, data?: unknown): void {
    this.bus.emit(event, data);
  }

  /** Tear down all modules */
  destroy(): void {
    for (const mod of this.modules) {
      try {
        mod.destroy();
      } catch (e) {
        log('warn', 'module destroy threw: ' + mod.name, e);
      }
    }
    this.modules = [];
    this.bus.destroy();
  }
}
